/**
 * OIDC Configuration Constants
 * Centralized configuration for OIDC authentication
 */
import { getEnv } from './env';

// Fallback OIDC Provider Configuration (Okta) - only used if env vars are unset.
// NOTE: this must stay in sync with the defaults in app-provider.tsx's oidcConfig,
// since oidc-client-ts derives its localStorage key from the *actual* authority/client_id
// the AuthProvider is constructed with (REACT_APP_OIDC_AUTHORITY / REACT_APP_OIDC_CLIENT_ID).
// Hardcoding this key previously caused "Switch Account" to silently fail in any
// environment (e.g. test) whose Okta authority/client_id differ from these fallbacks.
export const OKTA_CONFIG = {
  authority: 'https://integrator-3718641.okta.com/oauth2/default',
  client_id: '0oaw2uhxk2Ae0w1ov697',
} as const;

// Legacy OIDC Provider Configuration (Keycloak) - for backward compatibility
export const KEYCLOAK_CONFIG = {
  authority: 'http://localhost:8080/realms/approvn',
  client_id: 'approvn-frontend',
} as const;

/**
 * Get the OIDC storage key based on authority and client_id
 * Format: oidc.user:{authority}:{client_id}
 */
export function buildOidcStorageKey(authority: string, clientId: string): string {
  return `oidc.user:${authority}:${clientId}`;
}

/**
 * Get the authority/client_id this environment is actually configured to use,
 * reading from window._env_ (runtime) / process.env (build-time) exactly like
 * the AuthProvider in app-provider.tsx does, so the computed storage key always
 * matches the key oidc-client-ts uses under the hood.
 */
export function getConfiguredOidcClientConfig(): { authority: string; client_id: string } {
  return {
    authority: getEnv('REACT_APP_OIDC_AUTHORITY', OKTA_CONFIG.authority),
    client_id: getEnv('REACT_APP_OIDC_CLIENT_ID', OKTA_CONFIG.client_id),
  };
}

/**
 * Get the current OIDC storage key (Okta), derived from the environment's actual
 * configured authority/client_id rather than a hardcoded fallback.
 */
export function getOktaStorageKey(): string {
  const { authority, client_id } = getConfiguredOidcClientConfig();
  return buildOidcStorageKey(authority, client_id);
}

/**
 * Get the legacy OIDC storage key (Keycloak)
 */
export function getKeycloakStorageKey(): string {
  return buildOidcStorageKey(KEYCLOAK_CONFIG.authority, KEYCLOAK_CONFIG.client_id);
}

/**
 * Whether the "Switch Account" / "Add another account" UI is enabled.
 * Configurable per-environment via REACT_APP_ALLOW_ACCOUNT_SWITCHING
 * (runtime window._env_ or build-time .env). Defaults to enabled to
 * preserve existing behavior.
 */
export function isAccountSwitchingEnabled(): boolean {
  return getEnv('REACT_APP_ALLOW_ACCOUNT_SWITCHING', 'true').toLowerCase() === 'true';
}

/**
 * Get the active OIDC storage key
 * Checks which key has data in localStorage, defaulting to the configured Okta key.
 */
export function getActiveOidcStorageKey(): string {
  if (typeof window === 'undefined') {
    return getOktaStorageKey();
  }

  const oktaKey = getOktaStorageKey();
  const keycloakKey = getKeycloakStorageKey();

  // Check if Okta session exists
  if (localStorage.getItem(oktaKey)) {
    return oktaKey;
  }

  // Check if Keycloak session exists (backward compatibility)
  if (localStorage.getItem(keycloakKey)) {
    return keycloakKey;
  }

  // Diagnostic aid: if neither key has data, log every oidc.user:* key actually
  // present in localStorage so environment authority/client_id mismatches are
  // easy to spot (this was the root cause of switch-account failing in test).
  const presentOidcKeys = Object.keys(localStorage).filter(k => k.startsWith('oidc.user:'));
  if (presentOidcKeys.length > 0 && !presentOidcKeys.includes(oktaKey)) {
    console.warn('[oidc-config] No OIDC data found under computed key. This usually means ' +
      'REACT_APP_OIDC_AUTHORITY/REACT_APP_OIDC_CLIENT_ID do not match the key oidc-client-ts wrote.', {
      computedOktaKey: oktaKey,
      computedKeycloakKey: keycloakKey,
      actualOidcKeysInStorage: presentOidcKeys,
    });
  }

  // Default to Okta for new sessions
  return oktaKey;
}
